import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface PendingCompanyInvitation {
  id: string;
  company_id: string;
  company_name: string;
  inviter_name: string | null;
  expires_at: string | null;
  current_company_id: string | null;
  current_company_name: string | null;
  /** True when the invite can be accepted right now (no company, or an empty solo workspace). */
  can_join_directly: boolean;
}

/**
 * A live company invitation addressed to the signed-in user's email.
 * Resolved server-side so a missed link, a cleared localStorage token, or a
 * second login still surfaces the invitation instead of dead-ending.
 */
export function usePendingCompanyInvitation() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['pendingCompanyInvitation', user?.id],
    enabled: !!user,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PendingCompanyInvitation | null> => {
      const { data, error } = await supabase.rpc('get_my_pending_company_invitation');
      if (error) throw error;
      const payload = (data ?? {}) as { invitation?: PendingCompanyInvitation | null };
      return payload.invitation ?? null;
    },
  });

  return {
    invitation: query.data ?? null,
    isLoading: query.isLoading,
    refresh: () => queryClient.invalidateQueries({ queryKey: ['pendingCompanyInvitation', user?.id] }),
  };
}